import { useEffect, useMemo, useRef, useState } from 'react'
import * as Popover from '@radix-ui/react-popover'
import { Check, ChevronDown, GitBranch, Loader2, Search } from 'lucide-react'

export interface BranchPickerBranch {
  name: string
  default?: boolean
  /** ISO-8601 commit timestamp surfaced by the GitLab API; used to sort branches
   *  by recency when GitLab itself doesn't support order-by-updated on /branches. */
  committed_date?: string | null
}

interface BranchPickerProps {
  branches: BranchPickerBranch[]
  defaultBranchName?: string | null
  value: string
  onChange: (next: string) => void
  search: string
  onSearchChange: (next: string) => void
  loading?: boolean
  /** Whether the backend reports more pages of branches available. When true,
   *  the popover shows a "Load more" button at the bottom that calls onLoadMore. */
  hasMore?: boolean
  onLoadMore?: () => void
  loadingMore?: boolean
  disabled?: boolean
  className?: string
}

/**
 * Branch combobox: the native <select> we had before couldn't surface the
 * "1000 branches truncated, refine your search" hint that big GitLab repos
 * (exante-crm, friends) actually need, and it doesn't let us pin the
 * default branch above the alphabetical list of feature branches.
 *
 * Layout (top→bottom inside the popover):
 *   1) Search input (server-side filter, debounced upstream)
 *   2) Scrollable list with default branch pinned at the top
 *   3) "Showing first N — refine search to find more" footer when truncated
 */
export function BranchPicker({
  branches,
  defaultBranchName,
  value,
  onChange,
  search,
  onSearchChange,
  loading = false,
  hasMore = false,
  onLoadMore,
  loadingMore = false,
  disabled = false,
  className = '',
}: BranchPickerProps) {
  const [open, setOpen] = useState(false)
  const searchRef = useRef<HTMLInputElement | null>(null)

  // Pin the default at the top, then sort the rest by commit recency (most
  // recent first). GitLab's /branches endpoint doesn't support order-by-updated
  // server-side on this instance, so we sort client-side using each branch's
  // committed_date. Branches without committed_date (e.g. the catch-fallback
  // entry on API failure) sort to the bottom of the recency block.
  const orderedBranches = useMemo(() => {
    const resolvedDefault = defaultBranchName
      || branches.find((b) => b.default)?.name
      || null
    const head: BranchPickerBranch[] = []
    const tail: BranchPickerBranch[] = []
    for (const branch of branches) {
      if (resolvedDefault && branch.name === resolvedDefault) {
        head.push({ ...branch, default: true })
      } else {
        tail.push(branch)
      }
    }
    tail.sort((a, b) => {
      const aTime = a.committed_date ? Date.parse(a.committed_date) : 0
      const bTime = b.committed_date ? Date.parse(b.committed_date) : 0
      if (aTime === bTime) return a.name.localeCompare(b.name)
      return bTime - aTime
    })
    return [...head, ...tail]
  }, [branches, defaultBranchName])

  // The user might have typed a branch name (e.g. pasted from chat) that
  // isn't in the current paginated window; surface it as a placeholder row
  // so the popover doesn't appear "empty" when there's a valid selection.
  const showsOrphanSelection = value && !branches.some((b) => b.name === value)

  useEffect(() => {
    if (open) {
      // Defer to next tick so Popover.Content has mounted before we focus.
      const timer = window.setTimeout(() => searchRef.current?.focus(), 0)
      return () => window.clearTimeout(timer)
    }
    return undefined
  }, [open])

  return (
    <Popover.Root open={open} onOpenChange={disabled ? undefined : setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={`inline-flex items-center justify-between gap-2 px-2 py-1 rounded border border-border bg-background text-sm
            hover:border-foreground/20 focus:outline-none focus:ring-2 focus:ring-primary
            disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
        >
          <span className="inline-flex items-center gap-1.5 min-w-0">
            <GitBranch className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="truncate">{value || 'Select branch'}</span>
            {value && defaultBranchName === value ? (
              <span className="text-xs text-muted-foreground shrink-0">(default)</span>
            ) : null}
          </span>
          <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className="z-50 w-[min(420px,calc(100vw-2rem))] rounded-lg border border-border bg-background shadow-lg"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="w-4 h-4 text-muted-foreground absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search branches by name (e.g. release/, hotfix-, main)"
                className="w-full pl-8 pr-2 py-1.5 rounded border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto py-1" role="listbox">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading branches…
              </div>
            ) : orderedBranches.length === 0 ? (
              <div className="px-3 py-6 text-sm text-muted-foreground text-center">
                {search.trim()
                  ? `No branches match "${search.trim()}".`
                  : 'No branches found.'}
              </div>
            ) : (
              <>
                {showsOrphanSelection ? (
                  <button
                    type="button"
                    role="option"
                    aria-selected
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left bg-accent"
                    onClick={() => setOpen(false)}
                  >
                    <Check className="w-4 h-4 text-primary shrink-0" />
                    <span className="truncate">{value}</span>
                    <span className="text-xs text-muted-foreground ml-auto shrink-0">typed</span>
                  </button>
                ) : null}
                {orderedBranches.map((branch) => {
                  const isSelected = branch.name === value
                  return (
                    <button
                      type="button"
                      key={branch.name}
                      role="option"
                      aria-selected={isSelected}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-accent ${
                        isSelected ? 'bg-accent' : ''
                      }`}
                      onClick={() => {
                        onChange(branch.name)
                        setOpen(false)
                      }}
                    >
                      {isSelected ? (
                        <Check className="w-4 h-4 text-primary shrink-0" />
                      ) : (
                        <span className="w-4 h-4 shrink-0" />
                      )}
                      <span className="truncate">{branch.name}</span>
                      {branch.default ? (
                        <span className="text-xs text-muted-foreground ml-auto shrink-0">default</span>
                      ) : null}
                    </button>
                  )
                })}
              </>
            )}
          </div>

          {hasMore ? (
            <div className="px-3 py-2 border-t border-border flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">
                Showing {orderedBranches.length}; more available.
              </span>
              {onLoadMore ? (
                <button
                  type="button"
                  disabled={loadingMore}
                  onClick={(e) => {
                    e.stopPropagation()
                    onLoadMore()
                    // Refocus the search box after the click so keyboard users
                    // don't lose context to the load-more button.
                    requestAnimationFrame(() => searchRef.current?.focus())
                  }}
                  className="text-xs px-2 py-1 rounded border border-border bg-background hover:bg-accent
                    disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-1"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Loading…
                    </>
                  ) : (
                    'Load more'
                  )}
                </button>
              ) : null}
            </div>
          ) : null}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
